import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type IntlService from 'ember-intl/services/intl';
import type BetaToggleService from 'scaleflow/services/beta-toggle';
import type ScanModel from 'scaleflow/models/scan';
import { SCAN_SUB_STATUSES } from 'scaleflow/utils/scan-status';

type Args = {
  scans?: ScanModel[];
};

type Column = {
  name: string;
  valuePath: string;
  key: string;
  class: string;
  cellClass?: string;
  component?: string;
  test_id?: string;
  isSortable: boolean;
};

export default class ScansListTableComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare betaToggle: BetaToggleService;

  @tracked sorts = [];

  /**
   * The title column gives up 8 points to the two admin-only trailing columns
   * when they are present, so the row still adds up to 100% either way.
   */
  get columns(): Column[] {
    const isPlatformAdmin = this.betaToggle.isPlatformAdmin;

    const columns: Column[] = [
      {
        // Deliberately unlabelled: it leads the row as a bare signal. The dots
        // carry an aria-label per cell, so the empty header costs nothing to
        // screen readers. No `rotated-header` either — there is no text to turn.
        name: '',
        valuePath: 'status',
        key: 'status',
        class: '!w-[7%] header-center',
        cellClass: '!justify-center',
        component: 'scans/list-table/table-cell/status-dot',
        test_id: 'scan-status',
        isSortable: false,
      },
      {
        name: this.intl.t('dictionary.title'),
        valuePath: 'title',
        key: 'title',
        class: `${isPlatformAdmin ? '!w-[40%]' : '!w-[48%]'} text-left align-bottom`,
        cellClass: '!justify-start min-w-0',
        component: 'scans/list-table/table-cell/title',
        test_id: 'scan-title',
        isSortable: false,
      },
      ...SCAN_SUB_STATUSES.map((subStatus) => ({
        name: this.intl.t(subStatus.labelKey),
        valuePath: subStatus.key,
        key: subStatus.key,
        class: '!w-[9%] header-center rotated-header',
        cellClass: '!justify-center',
        component: 'scans/list-table/table-cell/status-dot',
        test_id: `scan-${subStatus.key}`,
        isSortable: false,
      })),
    ];

    if (isPlatformAdmin) {
      columns.push(
        {
          name: '',
          valuePath: 'details',
          key: 'details',
          class: '!w-[6%]',
          cellClass: '!justify-center !p-0 pr-1',
          component: 'scans/list-table/table-cell/details-link',
          test_id: 'scan-details',
          isSortable: false,
        },
        {
          name: '',
          valuePath: 'archive',
          key: 'archive',
          class: '!w-[6%]',
          cellClass: '!justify-center !p-0 pl-1',
          component: 'scans/list-table/table-cell/archive-action',
          test_id: 'scan-archive',
          isSortable: false,
        },
      );
    }

    return columns;
  }

  /**
   * Non-completed scans are only reachable by platform admins — the
   * `scans.scan.processing` route bounces everyone else back to the list — so
   * their titles render as plain text instead of links.
   */
  convertScanToRow(scan: ScanModel) {
    const row: Record<string, unknown> = {
      title: {
        id: scan.id,
        title: scan.title,
        isClickable:
          scan.status === 'COMPLETED' || this.betaToggle.isPlatformAdmin,
      },
      // The leading column is the row's headline, so its dot runs a size up.
      status: { status: scan.status, size: 'lg' },
      // Attached unconditionally: harmless when the admin columns are absent,
      // since ember-table only renders a cell for keys with a matching column.
      details: { id: scan.id },
      archive: { scan },
    };

    for (const { key } of SCAN_SUB_STATUSES) {
      row[key] = { status: scan[key as keyof ScanModel] as string };
    }

    return row;
  }

  get rows() {
    return (this.args.scans ?? []).map((scan) => this.convertScanToRow(scan));
  }
}
